import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Tabs } from '../components/ui/Tabs';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { AddContactModal } from '../components/organizations/AddContactModal';
import { mockOrganizations, mockStores } from '../utils/mockData';
import { calculateStoreStatus, getStatusColor } from '../utils/storeStatus';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/useToast';
interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
}
const TIMEZONES = [{
  value: 'America/New_York',
  label: 'Eastern Time (America/New_York)'
}, {
  value: 'America/Chicago',
  label: 'Central Time (America/Chicago)'
}, {
  value: 'America/Denver',
  label: 'Mountain Time (America/Denver)'
}, {
  value: 'America/Los_Angeles',
  label: 'Pacific Time (America/Los_Angeles)'
}, {
  value: 'America/Detroit',
  label: 'Eastern Time - Detroit (America/Detroit)'
}, {
  value: 'America/Phoenix',
  label: 'Mountain Time - Phoenix (America/Phoenix)'
}];
export function OrganizationDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const organization = mockOrganizations.find(o => o.id === id) || mockOrganizations[0];
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([{
    id: '1',
    name: organization.contactName,
    email: organization.contactEmail,
    phone: '(555) 123-4567',
    role: 'Primary Contact'
  }]);
  const [settings, setSettings] = useState({
    defaultTimezone: organization.defaultTimezone,
    defaultDeliveryAddress: organization.defaultDeliveryAddress,
    defaultFulfillmentMode: 'ship-to-home'
  });
  const organizationStores = mockStores.filter(s => s.organizationId === organization.id);
  const tabs = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'stores',
    label: 'Stores'
  }, {
    id: 'contacts',
    label: 'Contacts'
  }, {
    id: 'settings',
    label: 'Settings'
  }];
  const handleAddContact = (contact: any) => {
    const newContact = {
      ...contact,
      id: `contact_${Date.now()}`
    };
    setContacts(prev => [...prev, newContact]);
    showToast('Contact added successfully', 'success');
  };
  const handleRemoveContact = (contactId: string) => {
    setContacts(prev => prev.filter(c => c.id !== contactId));
    showToast('Contact removed', 'success');
  };
  const handleSaveSettings = () => {
    showToast('Settings updated successfully', 'success');
    setIsEditMode(false);
  };
  return <div className="space-y-6" data-id="element-889">
      <div className="flex items-center justify-between" data-id="element-890">
        <div className="flex items-center space-x-4" data-id="element-891">
          <Link to="/organizations" className="text-gray-500 hover:text-gray-700" data-id="element-892">
            <ArrowLeft className="h-5 w-5" data-id="element-893" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-894">
            {organization.name}
          </h1>
        </div>
        <Button onClick={() => navigate('/pop-up-stores/new')} data-id="element-895">
          <Plus className="h-4 w-4 mr-2" data-id="element-896" />
          Create Store
        </Button>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-897" />

      <div className="mt-6" data-id="element-898">
        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-899">
            <div className="lg:col-span-2" data-id="element-900">
              <Card title="Organization Details" action={<Button variant="outline" size="sm" onClick={() => setIsEditMode(!isEditMode)} data-id="element-902">
                    <Edit className="h-4 w-4 mr-1" data-id="element-903" />
                    Edit
                  </Button>} data-id="element-901">
                <div className="space-y-4" data-id="element-904">
                  <div className="grid grid-cols-2 gap-4" data-id="element-905">
                    <div data-id="element-906">
                      <p className="text-sm text-gray-600" data-id="element-907">Organization Name</p>
                      <p className="font-medium text-gray-900" data-id="element-908">
                        {organization.name}
                      </p>
                    </div>
                    <div data-id="element-909">
                      <p className="text-sm text-gray-600" data-id="element-910">Primary Contact</p>
                      <p className="font-medium text-gray-900" data-id="element-911">
                        {organization.contactName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-912">
                        {organization.contactEmail}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4" data-id="element-913">
                    <p className="text-sm text-gray-600 mb-2" data-id="element-914">
                      Default Timezone
                    </p>
                    <p className="font-medium text-gray-900" data-id="element-915">
                      {organization.defaultTimezone}
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4" data-id="element-916">
                    <p className="text-sm text-gray-600 mb-2" data-id="element-917">
                      Default Delivery Address
                    </p>
                    <p className="text-sm text-gray-900" data-id="element-918">
                      {organization.defaultDeliveryAddress}
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4" data-id="element-919">
                    <p className="text-sm text-gray-600 mb-2" data-id="element-920">
                      Default Fulfillment Mode
                    </p>
                    <p className="font-medium text-gray-900" data-id="element-921">Ship-to-home</p>
                  </div>
                </div>
              </Card>
            </div>

            <div data-id="element-922">
              <Card title="Quick Stats" data-id="element-923">
                <div className="space-y-4" data-id="element-924">
                  <div data-id="element-925">
                    <p className="text-sm text-gray-600" data-id="element-926">Total Stores</p>
                    <p className="text-3xl font-bold text-gray-900" data-id="element-927">
                      {organizationStores.length}
                    </p>
                  </div>
                  <div data-id="element-928">
                    <p className="text-sm text-gray-600" data-id="element-929">Active Stores</p>
                    <p className="text-3xl font-bold text-gray-900" data-id="element-930">
                      {organizationStores.filter(s => {
                    const status = calculateStoreStatus(s);
                    return status === 'open' || status === 'closing-soon' || status === 'scheduled';
                  }).length}
                    </p>
                  </div>
                  <div data-id="element-931">
                    <p className="text-sm text-gray-600" data-id="element-932">Total Orders</p>
                    <p className="text-3xl font-bold text-gray-900" data-id="element-933">
                      {organizationStores.reduce((sum, s) => sum + s.stats.ordersCount, 0)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>}

        {/* Stores Tab */}
        {activeTab === 'stores' && <Card title="Pop-Up Stores" action={<Button size="sm" onClick={() => navigate('/pop-up-stores/new')} data-id="element-935">
                <Plus className="h-4 w-4 mr-1" data-id="element-936" />
                Create New Store
              </Button>} data-id="element-934">
            {organizationStores.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-937">
                <p className="text-gray-500 mb-4" data-id="element-938">
                  No stores created yet for this organization.
                </p>
                <Button onClick={() => navigate('/pop-up-stores/new')} data-id="element-939">
                  <Plus className="h-4 w-4 mr-2" data-id="element-940" />
                  Create First Store
                </Button>
              </div> : <div className="overflow-x-auto" data-id="element-941">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-942">
                  <thead className="bg-gray-50" data-id="element-943">
                    <tr data-id="element-944">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-945">
                        Store Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-946">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-947">
                        Open Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-948">
                        Close Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-949">
                        Orders
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-950">
                        Revenue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-951">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-952">
                    {organizationStores.map(store => {
                const status = calculateStoreStatus(store);
                return <tr key={store.id} className="hover:bg-gray-50" data-id="element-953">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-954">
                            {store.name}
                          </td>
                          <td className="px-6 py-4" data-id="element-955">
                            <Badge variant={getStatusColor(status)} data-id="element-956">
                              {status.toUpperCase()}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-957">
                            {new Date(store.settings.openDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-958">
                            {new Date(store.settings.closeDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900" data-id="element-959">
                            {store.stats.ordersCount}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900" data-id="element-960">
                            ${store.stats.revenue.toFixed(2)}
                          </td>
                          <td className="px-6 py-4" data-id="element-961">
                            <Link to={`/pop-up-stores/${store.id}`} className="text-red-600 hover:text-red-700 text-sm font-medium" data-id="element-962">
                              View Store →
                            </Link>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}
          </Card>}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && <Card title="Contacts" action={<Button size="sm" onClick={() => setIsAddContactOpen(true)} data-id="element-964">
                <Plus className="h-4 w-4 mr-1" data-id="element-965" />
                Add Contact
              </Button>} data-id="element-963">
            <div className="space-y-3" data-id="element-966">
              {contacts.map(contact => <div key={contact.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg" data-id="element-967">
                  <div data-id="element-968">
                    <p className="font-medium text-gray-900" data-id="element-969">{contact.name}</p>
                    <p className="text-sm text-gray-600" data-id="element-970">{contact.email}</p>
                    <p className="text-sm text-gray-600" data-id="element-971">{contact.phone}</p>
                    {contact.role && <p className="text-xs text-gray-500 mt-1" data-id="element-972">
                        {contact.role}
                      </p>}
                  </div>
                  {contact.role !== 'Primary Contact' && <button onClick={() => handleRemoveContact(contact.id)} className="text-red-600 hover:text-red-700" data-id="element-973">
                      <Trash2 className="h-4 w-4" data-id="element-974" />
                    </button>}
                </div>)}
            </div>
          </Card>}

        {/* Settings Tab */}
        {activeTab === 'settings' && <Card title="Organization Settings" data-id="element-975">
            <div className="space-y-6" data-id="element-976">
              <Select label="Default Timezone" value={settings.defaultTimezone} onChange={e => setSettings(prev => ({
            ...prev,
            defaultTimezone: e.target.value
          }))} options={TIMEZONES} disabled={!isEditMode} data-id="element-977" />

              <Input label="Default Delivery Address" value={settings.defaultDeliveryAddress} onChange={e => setSettings(prev => ({
            ...prev,
            defaultDeliveryAddress: e.target.value
          }))} disabled={!isEditMode} data-id="element-978" />

              <Select label="Default Fulfillment Mode" value={settings.defaultFulfillmentMode} onChange={e => setSettings(prev => ({
            ...prev,
            defaultFulfillmentMode: e.target.value
          }))} options={[{
            value: 'ship-to-home',
            label: 'Ship-to-home'
          }, {
            value: 'bulk-ship',
            label: 'Bulk ship to school/organization'
          }, {
            value: 'hybrid',
            label: 'Hybrid (customer chooses)'
          }]} disabled={!isEditMode} data-id="element-979" />

              <div className="flex space-x-3" data-id="element-980">
                {!isEditMode ? <Button onClick={() => setIsEditMode(true)} data-id="element-981">
                    <Edit className="h-4 w-4 mr-2" data-id="element-982" />
                    Edit Settings
                  </Button> : <>
                    <Button onClick={handleSaveSettings} data-id="element-983">Save Changes</Button>
                    <Button variant="outline" onClick={() => setIsEditMode(false)} data-id="element-984">
                      Cancel
                    </Button>
                  </>}
              </div>
            </div>
          </Card>}
      </div>

      <AddContactModal isOpen={isAddContactOpen} onClose={() => setIsAddContactOpen(false)} onSave={handleAddContact} data-id="element-985" />
    </div>;
}